import { z } from "zod";
import { useUser } from "@/queries/user/useUser";
import { useGeoCountry } from "@/queries/countries/useGeoCountry";
import {
  ContactType,
  DefaultService as UsersService,
  PhoneNumberType,
  EmailType,
} from "@/api/SolarCloudApi";
import { toast } from "sonner";
import { FormCard2 } from "@/components/FormCard2";
import { useMutation } from "@tanstack/react-query";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/textarea";

const contactUsSchema = z
  .object({
    comment: z.string().min(1, "Please describe how we can help you"),
    phoneNumber: z.string().optional(),
    email: z.string().optional(),
  })
  .refine((data) => data.email || data.phoneNumber, {
    message: "Please provide either an email or phone number",
    path: ["email"],
  });

type TContactUs = z.infer<typeof contactUsSchema>;

export default function ContactUs2() {
  const { data: user } = useUser();
  const { data: defaultCountry } = useGeoCountry();

  const defaultPhoneNumber = user?.phoneNumbers?.find(
    (phone) => phone.type === PhoneNumberType.DEFAULT
  )?.number;

  const defaultEmail = user?.emails?.find(
    (email) => email.type === EmailType.DEFAULT
  )?.address;

  const defaultValues: TContactUs = {
    comment: "",
    phoneNumber: defaultPhoneNumber ?? "",
    email: defaultEmail ?? "",
  };

  const submitContact = useMutation({
    mutationFn: async (data: TContactUs) => {
      await UsersService.registerContact({
        type: ContactType.INQUIRY,
        givenName: user?.givenName,
        familyName: user?.familyName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        comment: data.comment,
        country: defaultCountry?.code ?? "AU",
      });
    },
    onSuccess: () => {
      toast.success(
        "Thank you for your enquiry. Someone will be in contact soon."
      );
    },
    onError: (err: any) => {
      toast.error(err.body?.message || err.message || "Inquiry failed");
    },
  });

  const onSubmit = (data: TContactUs) => {
    console.log("Submitting contact us form", data);
    // submitContact.mutate(data);
  };

  return (
    <FormCard2
      schema={contactUsSchema}
      options={{
        defaultValues,
      }}
      onSubmit={onSubmit}
      className="max-w-3xl"
      cardClassName="bg-transparent"
    >
      {(form) => (
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What can we help you with?</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Brief description about your request"
                    className="bg-brand-neutral-800 min-h-[120px]"
                    autoFocus
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Phone number"
                    {...field}
                    className="bg-brand-neutral-800"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Email"
                    {...field}
                    className="bg-brand-neutral-800"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end items-center gap-4">
            <Button
              disabled={submitContact.isPending}
              type="submit"
              className=""
            >
              Request Information
            </Button>
          </div>
        </div>
      )}
    </FormCard2>
  );
}
